// REACT IMPORTS
import React, { useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { isPasswordValid } from "utils/HelperFunctions";
import { useAppDispatch } from "store/hooks";
import {
  newPasswordSet,
  updateTokenExpiredStatus,
  updateTokenStatus,
} from "store/slices/authSlice";

// ICONS IMPORTS
import { CircleFillTickIcon } from "icons/success/CircleFillTickIcon";
import { CloseRingIcon } from "icons/subscription/CloseRingIcon";
import { onSetIsLogin } from "store/slices/commonSlice";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { useTid } from "utils/testid";

/* ------------------------------------- LOGIN VIA CRED FORM ------------------------------------- */

const SetPasswordForm = () => {
  const tid = useTid();
  const { state } = useLocation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { clusterId: cluster_id } = useParams();

  const [isLoading, setLoading] = useState<boolean>(false);

  const initialVal = {
    password: "",
  };

  const [values, setValues] = useState<any>(initialVal);

  const onSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    try {
      let payload = {
        ...values,
        accounts_id: state?.accounts_id,
        otpToken: state?.otpToken,
      };
      let res: any = await dispatch(newPasswordSet(payload)).unwrap();
      if (res && res?.body?.success) {
        localStorage.setItem(TOKEN_STORAGE_KEY, res?.body?.token);
        localStorage.setItem(TOKEN_STORAGE_TIME, `${new Date().getTime()}`);
        dispatch(updateTokenStatus(false));
        dispatch(updateTokenExpiredStatus(false));
        dispatch(onSetIsLogin(true));
        navigate(`/${cluster_id}/dashboard`);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.log("set pass err: -->", error?.message);
    }
  };

  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
  });

  const handleChange = (e: any) => {
    const password = e.target.value;
    setValues({ ...values, password });
    const requirements = isPasswordValid(password);
    setPasswordRequirements(requirements);
  };

  const checkIsDisabled = () => {
    let val = Object.values(values).every((value) => value === "");
    let isRequirementsEmpty = Object.values(passwordRequirements).every(
      (value) => value === true
    );
    return val || !isRequirementsEmpty;
  };

  return (
    <div
      className="animate__animated animate__fadeIn w-full lg:max-w-[520px]"
      data-testid={tid("form-shell")}
    >
      <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
        Set Your Password
      </div>
      <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph text-sm lg:text-base leading-normal font-normal">
        Please ensure your password meets the below criteria
      </p>
      <form onSubmit={onSubmit} data-testid={tid("set-password-form")}>
        <div className="max-w-[520px] flex flex-col gap-10 lg:gap-[60px]">
          <div className="flex flex-col lg:gap-3 gap-5">
            <InputField
              type="password"
              name="password"
              label="Password"
              data-testid={tid("password-input")}
              aria-label="Password"
              placeholder="Enter your password"
              value={values?.password}
              onChange={(e: any) => handleChange(e)}
              _className="!font-medium !text-base"
            />
            <div
              className="lg:p-4 py-4 px-3 bg-bgThird rounded-[7px] select-none"
              data-testid={tid("password-requirements")}
              aria-label="Password requirements"
            >
              <div>Password must contain at least:</div>
              <div className="pt-3">
                <div className="flex items-center gap-2">
                  {passwordRequirements?.length ? (
                    <CircleFillTickIcon aria-hidden="true" />
                  ) : (
                    <CloseRingIcon aria-hidden="true" />
                  )}
                  <div className="py-1 text-txtSecondary text-sm lg:text-base">
                    Minimum 8 characters long.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {passwordRequirements?.uppercase ? (
                    <CircleFillTickIcon aria-hidden="true" />
                  ) : (
                    <CloseRingIcon aria-hidden="true" />
                  )}
                  <div className="py-1 text-txtSecondary text-sm lg:text-base">
                    At least one uppercase letter (A-Z).
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {passwordRequirements?.lowercase ? (
                    <CircleFillTickIcon aria-hidden="true" />
                  ) : (
                    <CloseRingIcon aria-hidden="true" />
                  )}
                  <div className="py-1 text-txtSecondary text-sm lg:text-base">
                    At least one lowercase letter (a-z).
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {passwordRequirements?.digit ? (
                    <CircleFillTickIcon aria-hidden="true" />
                  ) : (
                    <CloseRingIcon aria-hidden="true" />
                  )}
                  <div className="py-1 text-txtSecondary text-sm lg:text-base">
                    At least one numerical digit (0-9).
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {passwordRequirements?.specialChar ? (
                    <CircleFillTickIcon aria-hidden="true" />
                  ) : (
                    <CloseRingIcon aria-hidden="true" />
                  )}
                  <div className="py-1 text-txtSecondary text-sm lg:text-base">
                    At least one special character (e.g., !, @, #, $, etc.)
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Button
            isLoading={isLoading}
            className="w-full"
            text="Sign Up"
            type="submit"
            data-testid={tid("sign-up-button")}
            aria-label="Complete sign up"
            isDisabled={checkIsDisabled()}
            variant="variable"
          />
        </div>
      </form>
      <div className="max-w-[520px] flex flex-col gap-4 items-center pt-4">
        <div className="flex gap-[6px] items-center justify-start w-full">
          <p className="lg:text-base text-sm text-txtParagraph leading-normal">
            Already have an account?
          </p>
          <Link
            to={`/${cluster_id}/login`}
            className="cursor-pointer lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
            data-testid={tid("login-link")}
            aria-label="Log in instead"
          >
            Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordForm;
